/**
 * TypeScript view of the `minesweeper` format.
 * Source of truth: `shared/schema/minesweeper.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface MinesweeperDefinition {
  puzzleType: "minesweeper";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One string per row: `.` a hidden cell, `0`-`8` a revealed cell and its number. */
  cells: string[];
  /** The total number of mines, public. */
  mines: number;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const HIDDEN = ".";
export const FLAG = "*";
export const EMPTY = ".";
/** A solving aid: the player is sure the cell holds no mine. Never sent to the server. */
export const SAFE = "x";
export type Mark = typeof FLAG | typeof SAFE | typeof EMPTY;

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface MinesweeperProgress {
  version: 1;
  /** One string per row, `*` a flag, `x` a cell marked safe, `.` undecided. */
  rows: string[];
}

/** One string per row, `*` a flagged mine and `.` anything else: safe marks fold into `.`. */
export interface MinesweeperSubmission {
  rows: string[];
}

/** The author's mine layout, in the submission format. */
export type MinesweeperSolution = MinesweeperSubmission;

export const MAX_SIZE = 30;

export type Cell = [row: number, col: number];

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
